import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CreateRenderedSummaryError,
  CreateRenderedSummaryRequest,
  CreateRenderedSummaryResponse,
  PatchRenderedSummaryError,
  PatchRenderedSummaryRequest,
  PatchRenderedSummaryResponse,
  RenderedSummariesError,
  RenderedSummariesRequest,
  RenderedSummariesResponse,
  RenderedSummaryError,
  RenderedSummaryRequest,
  RenderedSummaryResponse,
} from '@/api/model';

const KEY = {
  GET_RENDERED_SUMMARIES: 'GET_RENDERED_SUMMARIES',
  GET_RENDERED_SUMMARY: 'GET_RENDERED_SUMMARY',
} as const;

const request = {
  getRenderedSummary: async (
    { payload: { uuid } }: RenderedSummaryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<RenderedSummaryResponse>(
      `${ENDPOINTS.FCT.RENDERED_SUMMARY.BASE}${uuid}`,
      { signal },
    );

    return data;
  },
  getReportRenderedSummaries: async (
    { payload: { uuid } }: RenderedSummariesRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<RenderedSummariesResponse>(
      `${ENDPOINTS.FCT.RENDERED_SUMMARY.REPORT}${uuid}`,
      { signal },
    );

    return data;
  },
  patchRenderedSummary: async ({ payload }: PatchRenderedSummaryRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchRenderedSummaryResponse>(
      `${ENDPOINTS.FCT.RENDERED_SUMMARY.BASE}${payload.uuid}`,
      { body: payload },
    );

    return data;
  },
  postCreateRenderedSummary: async ({ payload }: CreateRenderedSummaryRequest) => {
    const { data } = await fetchClient.FCT.post<CreateRenderedSummaryResponse>(
      ENDPOINTS.FCT.RENDERED_SUMMARY.BASE,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetRenderedSummary: (
    { payload }: RenderedSummaryRequest,
    config?: Partial<UseQueryOptions<RenderedSummaryResponse, RenderedSummaryError>>,
  ) =>
    useQuery<RenderedSummaryResponse, RenderedSummaryError>({
      queryFn: ({ signal }) => request.getRenderedSummary({ payload }, signal),
      queryKey: [KEY.GET_RENDERED_SUMMARY, payload.uuid],
      ...config,
    }),
  useGetReportRenderedSummaries: (
    { payload }: RenderedSummariesRequest,
    config?: Partial<UseQueryOptions<RenderedSummariesResponse, RenderedSummariesError>>,
  ) =>
    useQuery<RenderedSummariesResponse, RenderedSummariesError>({
      enabled: !!payload.uuid,
      queryFn: ({ signal }) => request.getReportRenderedSummaries({ payload }, signal),
      queryKey: [KEY.GET_RENDERED_SUMMARIES, payload.uuid],
      ...config,
    }),
};

const mutation = {
  usePatchRenderedSummary: () =>
    useMutation<
      PatchRenderedSummaryResponse,
      PatchRenderedSummaryError,
      PatchRenderedSummaryRequest
    >({
      mutationFn: request.patchRenderedSummary,
    }),
  usePostCreateRenderedSummary: () =>
    useMutation<
      CreateRenderedSummaryResponse,
      CreateRenderedSummaryError,
      CreateRenderedSummaryRequest
    >({
      mutationFn: request.postCreateRenderedSummary,
    }),
};

export const renderedSummary = {
  KEY,
  mutation,
  query,
  request,
};
